import { useEffect, useRef } from 'react';

type UseNotIdleCallback = () => void;

type UseNotIdleOptions = {
  target?: HTMLElement | Document | Window | null;
  events: (keyof HTMLElementEventMap)[];
  delay?: number;
  enabled?: boolean;
};

const DEFAULT_DELAY_MS = 30_000;

export const useNotIdle = (
  callback: UseNotIdleCallback,
  { target, events, delay, enabled = true }: UseNotIdleOptions,
) => {
  const internalDelay = delay ?? DEFAULT_DELAY_MS;
  const hasActivityRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const callbackRef = useRef<UseNotIdleCallback>(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const handleActivity = () => {
    hasActivityRef.current = true;
  };

  const checkActivity = () => {
    if (hasActivityRef.current) {
      callbackRef.current();
      hasActivityRef.current = false;
    }
  };

  useEffect(() => {
    if (!enabled) return;

    const eventTarget = target ?? document;

    events.forEach((event) =>
      eventTarget.addEventListener(event, handleActivity, { passive: true }),
    );

    intervalRef.current = setInterval(checkActivity, internalDelay);

    return () => {
      events.forEach((event) =>
        eventTarget.removeEventListener(event, handleActivity),
      );

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [internalDelay, events, target, enabled]);
};
